import React, { useCallback, useState, useEffect } from 'react';

import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { TrashcanAnimation } from '@eika/trashcan-animation';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import type { EFaktura } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { AnalyticsEvents, trackEvent } from '../../../services/analytics';
import { deleteEFaktura } from '../../../services/eFakturaService';
import ActionResultInfo from '../../shared/ActionResultInfo/ActionResultInfo';

export enum DeletionStatus {
    INITIAL,
    CONFIRMING,
    LOADING,
    RESULT,
}

interface Props {
    eFaktura: EFaktura;
    deletionState: DeletionStatus;
    onDelete: () => void;
    setStatus: (status: DeletionStatus) => void;
}

const DeleteEFaktura: React.FC<Props> = ({ eFaktura, deletionState, setStatus, onDelete }) => {
    const [deletionError, setDeletionError] = useState();
    const translated = useTranslation();

    useEffect(() => {
        if (deletionState === DeletionStatus.CONFIRMING) {
            trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'delete_efaktura', 'start');
        }
    }, [deletionState]);

    const deleteEfaktura = useCallback(() => {
        setStatus(DeletionStatus.LOADING);
        deleteEFaktura(eFaktura.id)
            .then(onDelete)
            .catch((exception) => {
                setDeletionError(exception);
                trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'delete_efaktura', 'error');
            })
            .finally(() => {
                setStatus(DeletionStatus.RESULT);
                trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'delete_efaktura', 'complete');
            });
    }, [eFaktura.id, onDelete, setStatus]);

    const cancelDeletionDialog = useCallback(() => {
        setStatus(DeletionStatus.INITIAL);
        trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'delete_efaktura', 'cancel');
    }, [setStatus]);

    if (deletionState === DeletionStatus.RESULT) {
        return (
            <ActionResultInfo
                title={translated.eFaktura.isDeleted}
                retry={deleteEfaktura}
                error={deletionError}
                icon={<TrashcanAnimation className="transfer-success-checkmark" />}
            />
        );
    }

    return (
        <ConfirmationDialog
            title={translated.eFaktura.sureYouWantToDelete}
            id={`efaktura-slette-dialog-${eFaktura.id.value}`}
            className="efaktura-delete-dialog"
            footer={
                <>
                    {deletionState !== DeletionStatus.LOADING && (
                        <Button buttonType="link" onClick={cancelDeletionDialog}>
                            {translated.cancel}
                        </Button>
                    )}
                    <ButtonWithSpinner
                        size="small"
                        buttonType="positive"
                        onClick={deleteEfaktura}
                        showSpinner={deletionState === DeletionStatus.LOADING}
                    >
                        {translated.confirmDelete}
                    </ButtonWithSpinner>
                </>
            }
            show={deletionState === DeletionStatus.LOADING || deletionState === DeletionStatus.CONFIRMING}
            onExitFocus={cancelDeletionDialog}
            headingLevel={3}
        />
    );
};

export default DeleteEFaktura;
